import React from 'react';
import {
	DATE_AND_TIME,
	FixtureTypeLabels,
	VesselOwnershipTypeLabels,
} from '@shared/utils/constants';
import formatTcAndBbFixtureDuration from '@shared/utils/formatTcAndBbFixtureDuration';
import type { GetFixtureDetailsResponse } from '@api/features/fixtures/getFixtureDetails';
import type { TcFixtureProps } from '@api/models/tc-fixture';
import type { BbFixtureProps } from '@api/models/bb-fixture';
import { formatDate } from '@client/utils/formatDate';
import VesselDescription from '@client/components/VesselDescription';
import { DetailsItem } from '@client/components/Details';
import styles from '@client/screens/fixtures/shared/styles/FixtureDetailsScreen.module.css';

export type SummaryItemGroup = {
	header: string;
	items: DetailsItem[];
};

export const useTcSummaryItemGroups = ({
	fixture,
}: {
	fixture: GetFixtureDetailsResponse<TcFixtureProps | BbFixtureProps> | undefined;
}): SummaryItemGroup[] => {
	const vessel = fixture?.vessel;
	const counterParty = fixture?.tcTerms?.FixtureCounterparty;

	if (fixture == null) {
		return [];
	}

	return [
		{
			header: 'Estimate',
			items: [
				{
					key: 'fixtureType',
					label: 'Estimate Type',
					value: FixtureTypeLabels[fixture.type],
				},
				{
					key: 'identifier',
					label: 'Identifier',
					value: fixture.identifier,
				},
				{
					key: 'cpDate',
					label: 'CP Date',
					value: formatDate(fixture.tcTerms.cpDate),
				},
				{
					key: 'laycanFrom',
					label: 'Laycan From',
					value: formatDate(fixture.tcTerms.laycanFrom, DATE_AND_TIME, true),
				},
				{
					key: 'laycanTo',
					label: 'Laycan To',
					value: formatDate(fixture.tcTerms.laycanTo, DATE_AND_TIME, true),
				},
				{
					key: 'deliveryPortsOrRanges',
					label: 'Delivery Port',
					value: fixture.tcTerms.deliveryPortsOrRangesLabel,
				},
				{
					key: 'redeliveryPortsOrRanges',
					label: 'Redelivery Port',
					value: fixture.tcTerms.redeliveryPortsOrRangesLabel,
				},
				{
					key: 'deliveryRange',
					label: 'Delivery Range',
					value: fixture.tcTerms.deliveryRange,
				},
				{
					key: 'redeliveryRange',
					label: 'Redelivery Range',
					value: fixture.tcTerms.redeliveryRange,
				},
				{
					key: 'minDuration',
					label: 'Min. Duration',
					value: (fixture.tcTerms.laycanTo != null && fixture.PeriodScheduleItems != null) ? (
						<>
							{formatTcAndBbFixtureDuration({
								items: fixture.PeriodScheduleItems,
								laycanFrom: fixture.tcTerms.laycanFrom,
								laycanTo: fixture.tcTerms.laycanTo,
							})?.split(' - ')[0]}
							<br />
							<span className={styles.smallDate}>
								{formatDate(fixture.estimatedRedeliveryDate ?? null)}
							</span>
						</>
					) : null,
				},
				{
					key: 'maxDuration',
					label: 'Max. Duration',
					value: (fixture.tcTerms.laycanTo && fixture.tcTerms.laycanFrom && fixture.PeriodScheduleItems != null) ? (
						<>
							{formatTcAndBbFixtureDuration({
								items: fixture.PeriodScheduleItems,
								laycanFrom: fixture.tcTerms.laycanFrom,
								laycanTo: fixture.tcTerms.laycanTo,
							})?.split(' - ')[1]}
							<br />
							<span className={styles.smallDate}>
								{formatDate(fixture.maxRedeliveryDate ?? null)}
							</span>
						</>
					) : null,
				},
			],
		},
		{
			header: 'Vessel',
			items: [
				{
					key: 'vesselName',
					label: 'Name',
					value: vessel?.name == null ? null : (
						<VesselDescription
							name={vessel.name}
							flag={vessel.flag}
						/>
					),
				},
				{
					key: 'vesselOwnership',
					label: 'Ownership',
					value: vessel == null ? null : VesselOwnershipTypeLabels[vessel.ownershipType],
				},
				{
					key: 'vesselClass',
					label: 'Class',
					value: vessel?.vesselClass,
				},
			],
		},
		{
			header: 'Counterparty',
			items: [
				{
					key: 'counterpartyName',
					label: 'Name',
					value: counterParty?.name,
				},
			],
		},
	];
};
